import React from 'react'
import { AppContext } from "contexts/AppContext";
import { useEffect, useContext, useState } from 'react'
import { callAPI } from "utils/API";
import "./Register.css"
import Translate, { translate } from 'utils/Translate';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import ContentNormal from 'components/elements/ContentNormal';
import ButtonLarge1 from 'components/elements/ButtonLarge1';


const RegisterForm = ({setError, setSuccess, setMobilePhoneNumber, modern = false}) => {
    const { brandData, styleGuide } = useContext(AppContext);
    const [departments,setDepartments]=useState([])
    const [fieldForm, setFieldForm] = useState({phoneCountryCode:'+45'})
    const [showErrors, setShowErrors] = useState(false);
    const [touchedFields, setTouchedFields] = useState({});
    
    const registerUserFeatures = brandData && brandData.features.registerUser;
    const description = brandData && brandData.features.registerUser && brandData.features.registerUser.description;
    const header = registerUserFeatures && registerUserFeatures.header
    const consent = registerUserFeatures && registerUserFeatures.consent;
    const enterCvr = registerUserFeatures && registerUserFeatures.enterCvr;
    const showDepartmentOptions = registerUserFeatures && registerUserFeatures.showDepartmentOptions;
    const enterUserUid = registerUserFeatures && registerUserFeatures.enterUserUid;
    const companyIsOptional = registerUserFeatures && registerUserFeatures.companyIsOptional;
    
    const getUserDepartments = async() => {
        let result = await callAPI('/user/departments', 'GET')
        if (result) {
            setDepartments(result)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (isFormValid) {
            const {
                name,
                email,
                mobilePhoneNumber,
                companyAddress,
                companyPostalCode,
                companyCity,
                companyDepartment,
                termsAndConditionsAccepted,
                phoneCountryCode,
                userUID,
                userCompanyUID
            } = fieldForm
            const body = {
                userName: name,
                userEmail: email.trim(),
                userPhone: `${phoneCountryCode || '+45'}${mobilePhoneNumber}`.trim(),
                userAddress: companyAddress,
                userZipcode: companyPostalCode,
                userCity: companyCity,                
                termsAndConditionsAccepted,
                userUID
            }
            if(enterCvr) {
                body.userCompany = companyDepartment
                body.userCompanyUID = userCompanyUID
            } else if (brandData?.features?.registerUser?.setCompanyAsDepartment) {
                body.userDepartment = companyDepartment
            } else {
                body.userCompany = companyDepartment
            }
            const response = await callAPI(`/user/register`, "POST", {body});
            if (response.status === 'ok') {
                setMobilePhoneNumber(mobilePhoneNumber)
                setSuccess(true)
            }else {
                setError(true)
            }
        } else {
            setShowErrors(true)
        }
    }

    const handleInputChange = (e) => {
        var target = e.target
        const value = target.type === 'checkbox' ? target.checked : target.value
        const data = { ...fieldForm, [target.name]: value };
        setFieldForm(data);
        if (enterCvr && target.name === 'userCompanyUID' && /^\d{8}$/.test(value)) {
            getPublicCVRInformation(value);
        }
    }

    const getPublicCVRInformation = async (userCompanyUID) => {
        if (!userCompanyUID) {
            console.log("No CVR number provided");
            return;
        }

        try {
            let companyInformation = await callAPI(`/cvr/${userCompanyUID}`, "GET");
            
            if (!companyInformation) {
                console.log("No data could be found");
            } else {
                setFieldForm(current => current.userCompanyUID !== userCompanyUID ? current : {
                    ...current,
                    companyAddress: companyInformation.address,
                    companyPostalCode: String(companyInformation.zipcode ?? ''),
                    companyCity: companyInformation.city,
                    companyDepartment: showDepartmentOptions ? current.companyDepartment : companyInformation.name,
                });
            }
        } catch (error) {
            console.log("Error fetching CVR information", error);
        }
    };

    const validateForm = (data) => {
        const {
            name,
            email,
            mobilePhoneNumber,
            companyAddress,
            companyPostalCode,
            companyCity,
            companyDepartment,
            termsAndConditionsAccepted,
            phoneCountryCode,
            userUID,
            userCompanyUID
        } = data
    
        const userNameRegex = /^([^0-9]*)$/;
        const phoneNumberRegex = /^([+]?)([0-9]*)$/
        const emailRegex = modern ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/ : /^[^ ]+@[^ ]+\.[a-z]{2,3}$/;
        const hasText = value => !!String(value ?? '').trim();
    
        const validfields = {
            name: (modern ? hasText(name) : !!name) && userNameRegex.test(name),
            email: !!email && emailRegex.test(modern ? email.trim() : email),
            mobilePhoneNumber: modern ? /^\d{8}$/.test(mobilePhoneNumber || '') : !!mobilePhoneNumber && phoneNumberRegex.test(mobilePhoneNumber),
            phoneCountryCode: phoneCountryCode ? phoneNumberRegex.test(phoneCountryCode) : true, // empty is also valid will auto add +45 on submit
            companyAddress: modern ? hasText(companyAddress) : !!companyAddress,
            companyPostalCode: modern ? /^\d{4}$/.test(companyPostalCode || '') : !!companyPostalCode,
            companyCity: modern ? hasText(companyCity) : !!companyCity,
            companyDepartment: !!companyIsOptional || (modern ? hasText(companyDepartment) : !!companyDepartment),
            termsAndConditionsAccepted: !!termsAndConditionsAccepted
        }
        if(enterUserUid){
            validfields.userUID = modern ? hasText(userUID) : !!userUID
        }

        if (modern && enterCvr) {
            validfields.userCompanyUID = /^\d{8}$/.test(userCompanyUID || '');
        }
        if (modern && showDepartmentOptions && hasText(companyDepartment)) {
            validfields.companyDepartment = departments.some(department => department.departmentName === companyDepartment);
        }

        return validfields;
    }

    const validFields = validateForm(fieldForm);
    const isFormValid = Object.values(validFields).every(Boolean);

    useEffect(() => {
        if(showDepartmentOptions){
            getUserDepartments()
        }
    }, [])

    if (modern) {
        const formatMessages = {
            name: <Translate translationKey="_REGISTER_FORM_NAME_INVALID_">Indtast dit navn uden tal.</Translate>,
            email: <Translate translationKey="_REGISTER_FORM_EMAIL_INVALID_">Indtast en gyldig e-mailadresse.</Translate>,
            mobilePhoneNumber: <Translate translationKey="_REGISTER_FORM_PHONE_INVALID_">Mobilnummeret skal bestå af 8 cifre.</Translate>,
            companyPostalCode: <Translate translationKey="_REGISTER_FORM_ZIP_INVALID_">Postnummeret skal bestå af 4 cifre.</Translate>,
            userCompanyUID: <Translate translationKey="_REGISTER_FORM_CVR_INVALID_">CVR-nummeret skal bestå af 8 cifre.</Translate>,
        };
        const field = (name, id, labelKey, label, props = {}) => {
            const error = (showErrors || !!touchedFields[name]) && !validFields[name];
            return (
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    name={name}
                    id={id}
                    label={<Translate translationKey={labelKey}>{label}</Translate>}
                    value={fieldForm[name] || ""}
                    onChange={handleInputChange}
                    onBlur={() => setTouchedFields(current => ({ ...current, [name]: true }))}
                    required={name !== "companyDepartment" || !companyIsOptional}
                    error={error}
                    helperText={error
                        ? formatMessages[name] || <Translate translationKey="_REGISTER_FORM_FIELD_REQUIRED_">Udfyld dette felt.</Translate>
                        : name === "companyDepartment" && companyIsOptional
                            ? <Translate translationKey="_REGISTER_FORM_FIELD_OPTIONAL_">Valgfrit</Translate>
                            : undefined}
                    {...props}
                />
            );
        };

        return (
            <Stack
                component="form"
                className="register-form-modern"
                onSubmit={handleSubmit}
                noValidate
                spacing="var(--auth-field-gap)"
                sx={{
                    pt: 1,
                    minWidth: 0,
                    "& .MuiInputBase-root": { minHeight: 44, fontFamily: "inherit", borderRadius: "10px", fontSize: 16 },
                    "& .MuiInputBase-input": { py: "var(--auth-input-padding)" },
                    "& .MuiFormLabel-root": { fontFamily: "inherit", fontSize: 16 },
                    "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                        transform: "translate(14px, var(--auth-input-padding)) scale(1)",
                    },
                    "& .MuiFormHelperText-root": { fontFamily: "inherit" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: styleGuide.color16,
                    },
                    "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": { color: styleGuide.color16 },
                }}
            >
                {field("name", "register-form-name", "_REGISTER_FORM_NAME_LABEL_", "Navn", { autoComplete: "name" })}
                {enterUserUid && field("userUID", "register-form-uid", "_REGISTER_FORM_UID_LABEL_", "Medarbejdernummer")}
                {field("email", "register-form-email", "_REGISTER_FORM_EMAIL_LABEL_", "Arbejds e-mail", {
                    type: "email",
                    autoComplete: "email",
                })}
                {field("mobilePhoneNumber", "register-form-mobilePhoneNumber", "_REGISTER_FORM_MOBILE_LABEL_", "Mobilnummer", {
                    type: "tel",
                    autoComplete: "tel-national",
                    slotProps: {
                        htmlInput: { maxLength: 8, inputMode: "numeric", pattern: "[0-9]{8}" },
                        input: { startAdornment: <InputAdornment position="start">+45</InputAdornment> },
                    },
                })}
                {!!enterCvr && field("userCompanyUID", "register-form-companyUID", "_REGISTER_FORM_CVR_LABEL_", "CVR", {
                    slotProps: { htmlInput: { maxLength: 8, inputMode: "numeric", pattern: "[0-9]{8}" } },
                })}
                {field("companyDepartment", "register-form-companyDepartment", "_REGISTER_FORM_COMPANY_LABEL_", "Virksomhed", {
                    select: !!showDepartmentOptions,
                    ...(showDepartmentOptions ? {
                        children: [
                            <option key="" value=""><Translate translationKey="_REGISTER_FORM_DEPARTMENT_PLACEHOLDER_">Vælg afdeling</Translate></option>,
                            ...departments.map(department => (
                                <option key={department.departmentName} value={department.departmentName}>
                                    {department.departmentName}
                                </option>
                            )),
                        ],
                        slotProps: { select: { native: true }, inputLabel: { shrink: true } },
                    } : { autoComplete: "organization" }),
                })}
                {field("companyAddress", "register-form-companyAddress", "_REGISTER_FORM_ADDRESS_LABEL_", "Adresse", {
                    autoComplete: "street-address",
                })}
                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 2 }}>
                    {field("companyPostalCode", "register-form-companyPostalCode", "_REGISTER_FORM_POSTNUMBER_LABEL_", "Postnr", {
                        autoComplete: "postal-code",
                        slotProps: { htmlInput: { maxLength: 4, inputMode: "numeric", pattern: "[0-9]{4}" } },
                    })}
                    {field("companyCity", "register-form-companyCity", "_REGISTER_FORM_CITY_LABEL_", "By", {
                        autoComplete: "address-level2",
                    })}
                </Box>
                <FormControl error={showErrors && !validFields.termsAndConditionsAccepted}>
                    <FormControlLabel
                        sx={{ m: 0, alignItems: "flex-start", gap: 1 }}
                        control={
                            <Checkbox
                                required
                                name="termsAndConditionsAccepted"
                                id="register-form-rules-checkbox"
                                checked={!!fieldForm.termsAndConditionsAccepted}
                                onChange={handleInputChange}
                                sx={{ p: 0, "&.Mui-checked": { color: styleGuide.color16 } }}
                                slotProps={{ input: { "aria-describedby": showErrors && !validFields.termsAndConditionsAccepted ? "register-consent-error" : undefined } }}
                            />
                        }
                        label={
                            <Typography component="span" sx={{ display: "block", fontFamily: "inherit", fontSize: 13, lineHeight: 1.5 }}>
                                {!!consent
                                    ? <Box component="span" sx={{ "& p": { m: 0 } }} dangerouslySetInnerHTML={{ __html: consent }} />
                                    : 'Jeg giver hermed samtykke til at Tryg må videregive mine oplysninger til brug i Tryg Pointshop (navn, adresse, medarbejdernummer,telefonnummer, e-mail og afdeling) til brug for oprettelse, administration, tildeling af point samt eventuel overførsel af point fratidligere pointshop'}
                            </Typography>
                        }
                    />
                    {showErrors && !validFields.termsAndConditionsAccepted && (
                        <FormHelperText id="register-consent-error">
                            <Translate translationKey="_REGISTER_FORM_CONSENT_REQUIRED_">Du skal acceptere betingelserne for at fortsætte.</Translate>
                        </FormHelperText>
                    )}
                </FormControl>
                <Button
                    type="submit"
                    disabled={!isFormValid}
                    variant="contained"
                    fullWidth
                    disableElevation
                    sx={{
                        minHeight: 48,
                        borderRadius: "10px",
                        fontFamily: "inherit",
                        fontSize: 16,
                        fontWeight: 600,
                        textTransform: "none",
                        bgcolor: styleGuide.color16,
                        color: styleGuide.color1,
                        "&:hover": { bgcolor: styleGuide.color17 },
                    }}
                >
                    <Translate translationKey="_REGISTER_FORM_BUTTON_LABEL_">Indsend</Translate>
                </Button>
            </Stack>
        );
    }

    return (
        <Box className={modern ? "register-form-modern" : undefined} style={{ minHeight: modern ? undefined : "64vh" }} minWidth={modern ? 0 : 320}>
            {!modern && !!header && <div style={{margin:'auto',width:'70%',justifyContent:'center'}}>
                <h5 className="register_headline">{header}</h5>
            </div>}
            {!modern && !!description && <div>
                <div style={{ width: "45px", height: "2px", margin: "auto", marginBottom: "1rem", marginTop: "1.5rem" }}></div>
                <h6
                    className='description_message'
                    dangerouslySetInnerHTML={{ __html: description }}>
                </h6> 
            </div>}
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <div>
                        <label className="label_style" htmlFor='register-form-name'>{translate("_REGISTER_FORM_NAME_LABEL_", "Navn:")}</label>
                        <input name="name" id="register-form-name" placeholder={translate("_REGISTER_FORM_NAME_PLACEHOLDER_", "Navn")} type="text" className={showErrors && !validFields.name?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)}/>
                    </div>
                    {enterUserUid && <div>
                        <label className="label_style" htmlFor='register-form-uid'>{translate("_REGISTER_FORM_UID_LABEL_", "Medarbejdernummer")}</label>
                        <input name="userUID" id='register-form-uid' placeholder={translate("_REGISTER_FORM_UID_PLACEHOLDER_", "Medarbejdernummer")} type="text" className={showErrors && !validFields.userUID?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)}/>
                    </div>}
                    <div>
                        <label className="label_style" htmlFor='register-form-email'>{translate("_REGISTER_FORM_EMAIL_LABEL_", "Arbejds e-mail:")}</label>
                        <input name="email" id='register-form-email' placeholder={translate("_REGISTER_FORM_EMAIL_PLACEHOLDER_", "Arbejds e-mail")} type="email" className={showErrors && !validFields.email?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)}/>
                    </div>
                    <div>
                        <label className="label_style" htmlFor='register-form-mobilePhoneNumber'>{translate("_REGISTER_FORM_MOBILE_LABEL_", "Mobilnummer:")}</label>
                        <div className="mobile_controller ">
                            <div>
                                <input name="phoneCountryCode" id='register-form-phoneCountryCode' disabled type="text" className="form-control country_code" value={"+45"} />
                            </div>
                            <div className='mobile_input'>
                                <input name="mobilePhoneNumber" id='register-form-mobilePhoneNumber' placeholder={translate("_REGISTER_FORM_MOBILE_PLACEHOLDER_", "Mobilnummer")} type="text" maxLength={8} className={showErrors && !validFields.mobilePhoneNumber?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)} />
                            </div>
                        </div>
                    </div>
                    {!!enterCvr && <div>
                        <label className="label_style" htmlFor='register-form-companyUID'>{translate("_REGISTER_FORM_CVR_LABEL_", "CVR:")}</label>
                        <input name="userCompanyUID" id='register-form-companyUID' placeholder={translate("_REGISTER_FORM_CVR_PLACEHOLDER_", "CVR")} type="text" className="form-control" onChange={(e)=>handleInputChange(e)} />
                    </div>}
                    <div>
                        <label className="label_style" htmlFor='register-form-companyDepartment'>{translate("_REGISTER_FORM_COMPANY_LABEL_", "Virksomhed:")}</label>
                        <div>
                            {showDepartmentOptions ? <select style={{width:'100%'}} name="companyDepartment" id='register-form-companyDepartment' placeholder="Afdeling" className={showErrors && !validFields.companyDepartment?"is_invalid":"form-control selector_div"} onChange={(e)=>handleInputChange(e)}>
                                <option>Vælg afdeling</option>
                                {departments && departments.length && (departments.map((e) => <option value={e.departmentName}>{ e.departmentName}</option>))}
                            </select> :
                                <input name="companyDepartment" value={fieldForm.companyDepartment || ""} id='register-form-companyDepartment' placeholder={translate("_REGISTER_FORM_COMPANY_PLACEHOLDER_", "Virksomhed")} type="text" className={showErrors && !validFields.companyDepartment?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)} />
                            }
                        </div>
                    </div>
                    {<div>
                        <label className="label_style" htmlFor='register-form-companyAddress'>{translate("_REGISTER_FORM_ADDRESS_LABEL_", "Adresse:")}</label>
                        <input name="companyAddress"  value={fieldForm.companyAddress || ""}  id='register-form-companyAddress' placeholder={translate("_REGISTER_FORM_ADDRESS_PLACEHOLDER_", "Adresse")} type="text" className={showErrors && !validFields.companyAddress?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)} />
                    </div>}
                    <div className="mobile_controller">
                        <div>
                            <label className="label_style" htmlFor='register-form-companyPostalCode'>{translate("_REGISTER_FORM_POSTNUMBER_LABEL_", "Postnr:")}</label>
                            <input name="companyPostalCode" value={fieldForm.companyPostalCode || ""}  id='register-form-companyPostalCode' placeholder={translate("_REGISTER_FORM_POSTNUMBER_PLACEHOLDER_", "Postnr")} type="text" className={showErrors && !validFields.companyPostalCode?"is_invalid":"form-control postal_div"} onChange={(e)=>handleInputChange(e)} />
                        </div>
                        <div className='address_div'>
                            <label className="label_style" htmlFor='register-form-companyCity'>{translate("_REGISTER_FORM_CITY_LABEL_", "By:")}</label>
                            <input name="companyCity" value={fieldForm.companyCity || ""} id='register-form-companyCity' placeholder={translate("_REGISTER_FORM_CITY_PLACEHOLDER_", "By")} type="text" className={showErrors  && !validFields.companyCity?"is_invalid":"form-control"} onChange={(e)=>handleInputChange(e)} />
                        </div>
                    </div>
                    <div className='mobile_controller' style={{paddingTop: 20, alignItems: modern ? 'flex-start' : 'center', display: 'flex', gap: 10}}>
                        <div>
                            <input required name="termsAndConditionsAccepted" id='register-form-rules-checkbox' type="checkbox" className="custom-control-input" aria-labelledby="register-consent-text" onChange={(e)=>handleInputChange(e)} />
                        </div>
                        <div id="register-consent-text">
                            <ContentNormal className="terms_condition">
                                {!!consent 
                                ?  <h6 className='description_message' dangerouslySetInnerHTML={{ __html: consent }} />
                                : 'Jeg giver hermed samtykke til at Tryg må videregive mine oplysninger til brug i Tryg Pointshop (navn, adresse, medarbejdernummer,telefonnummer, e-mail og afdeling) til brug for oprettelse, administration, tildeling af point samt eventuel overførsel af point fratidligere pointshop'}
                            </ContentNormal>
                        </div>
                    </div>
                    <div className="black_button_div">
                        <ButtonLarge1
                            translationKey="_REGISTER_FORM_BUTTON_LABEL_"
                            fallbackText="Indsend"
                            showIcon={false}
                        />
                    </div>
                </div>
            </form>
        </Box>
    )
}

export default RegisterForm